import type { CSSProperties } from 'react';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { Transcription } from '@frameio/graphql-client/generated';
import {
  Button2,
  Menu,
  MenuGroup,
  MenuRadio,
  MenuRadioGroup,
  MenuTextField,
  theme,
} from '@frameio/vapor';
import { IconChevronDown, IconMagnifyingglass } from '@frameio/vapor-icons';
import { inputLocaleMap, type Language, type LocaleKey } from './consts';
import TEXT from './generated/TEXT/LanguageMenu';

const DROPDOWN_TOTAL_HEIGHT = 249;
const SEARCH_HEIGHT = 35;
const DROPDOWN_SCROLLABLE_HEIGHT = DROPDOWN_TOTAL_HEIGHT - SEARCH_HEIGHT;
const DROPDOWN_WIDTH = 178;

const SELECT_DROPDOWN_WIDTH = 60;

TEXT.DEFINE({
  /** Text for language menu selection */
  chooseLanguage: 'Choose language',
  /** Text for selecting which lanuage is associated with the caption file */
  select: 'Select',
  /** Placeholder for language seach bar */
  placeholder: 'Search…',
  /** Accessible text describing the selected language */
  visuallyHidden: (language: Language) => `Selected language: ${language}`,
});

type LanguageMenuProps = {
  transcriptions: Transcription[];
  isAddLanguageMode: boolean;
  selectedLanguage: string | null;
  initialLanguage?: string | null;
  selectLocale: (locale: string) => void;
  mode?: 'upload' | 'transcribe';
  style?: CSSProperties;
  isDisabled?: boolean;
  menuPlacement?: 'bottom-start' | 'bottom';
};

export const LanguageMenu = ({
  transcriptions,
  isAddLanguageMode,
  isDisabled,
  selectedLanguage,
  initialLanguage,
  selectLocale,
  mode = 'transcribe',
  style,
  menuPlacement = 'bottom',
}: LanguageMenuProps) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<any>(null);
  const isAutoDetectLanguageFFEnabled = useFeatureFlagEnabled(
    'v4.auto_language_detection'
  );

  const sortedLanguageOptions = useMemo(
    () =>
      Object.entries(inputLocaleMap)
        .reduce(
          (acc, [code, { language }]) => {
            if (
              (isAutoDetectLanguageFFEnabled && mode === 'transcribe') ||
              code !== 'auto'
            ) {
              acc.push({ code, language });
            }
            return acc;
          },
          [] as { code: string; language: string }[]
        )
        .sort((a, b) => {
          if (isAutoDetectLanguageFFEnabled && a.code === 'auto') return -1;
          return a.language.localeCompare(b.language);
        }),
    [isAutoDetectLanguageFFEnabled, mode]
  );

  const displaySelectedLanguage = useMemo(() => {
    if (isAddLanguageMode && !selectedLanguage) {
      if (mode === 'upload') return TEXT.select;
      return TEXT.chooseLanguage;
    }

    if (isAddLanguageMode && selectedLanguage)
      return inputLocaleMap[selectedLanguage as LocaleKey]?.language;
    return inputLocaleMap[initialLanguage as LocaleKey]?.language;
  }, [isAddLanguageMode, selectedLanguage, initialLanguage, mode]);

  const filteredLanguageOptions = useMemo(
    () =>
      sortedLanguageOptions.filter(({ language }) => {
        return language.toLowerCase().includes(query.toLowerCase());
      }),
    [query, sortedLanguageOptions]
  );

  const languageOptions = useMemo(() => {
    return filteredLanguageOptions?.length > 0
      ? filteredLanguageOptions
      : sortedLanguageOptions;
  }, [filteredLanguageOptions, sortedLanguageOptions]);

  const disableLocale = (l: string) =>
    transcriptions.findIndex(
      ({ locale, source, displayName }) =>
        (locale === l || l === displayName?.toLowerCase()) &&
        source === 'GENERATED'
    ) > -1;

  return (
    <Menu
      content={
        <>
          <MenuGroup>
            <MenuTextField
              ref={inputRef}
              adornmentBefore={<IconMagnifyingglass size={16} />}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(e.target.value);
              }}
              placeholder={TEXT.placeholder}
              value={query}
            />
          </MenuGroup>
          <MenuRadioGroup
            checked={
              isAddLanguageMode
                ? (selectedLanguage as LocaleKey)
                : (initialLanguage as LocaleKey)
            }
            maxHeight={`${DROPDOWN_SCROLLABLE_HEIGHT}px`}
            onChange={selectLocale}
          >
            <MenuRadio
              disabled={true}
              name={TEXT.chooseLanguage}
              value={TEXT.chooseLanguage}
            >
              {TEXT.chooseLanguage}
            </MenuRadio>

            {languageOptions.map(({ code, language }) => (
              <MenuRadio
                key={code}
                disabled={mode === 'transcribe' ? disableLocale(code) : false}
                name={code}
                value={code}
              >
                {language}
              </MenuRadio>
            ))}
          </MenuRadioGroup>
        </>
      }
      initialFocusRef={inputRef}
      placement={menuPlacement}
    >
      <StyledButton
        $mode={mode ?? 'transcribe'}
        $style={style ?? {}}
        aria-label={TEXT.visuallyHidden(
          displaySelectedLanguage as Language
        )?.toString()}
        data-testid={`select-language-${mode}`}
        iconAfter={
          <IconChevronDown color={theme.colors.icon.secondary} size={16} />
        }
        isDisabled={isDisabled}
        spaceBetween={true}
      >
        {displaySelectedLanguage}
      </StyledButton>
    </Menu>
  );
};

export const StyledButton = styled(Button2)<{
  $mode?: string;
  $style?: CSSProperties;
}>`
  align-items: space-between;
  max-width: ${DROPDOWN_WIDTH}px;
  min-width: ${({ $mode }) =>
    $mode === 'upload' ? `${SELECT_DROPDOWN_WIDTH}px` : `${DROPDOWN_WIDTH}px`};
  ${({ $style }) => $style && { ...$style }};
`;
